import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Phone } from "lucide-react";
import SmoothScroll from "@/components/site/SmoothScroll";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";
import NotFound from "@/pages/NotFound";
import { Reveal, FadeUp } from "@/components/site/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { resolveImg, useGuideCategories } from "@/hooks/usePublicContent";
import { CONTACT } from "@/data/content";
import { useQuote } from "@/context/QuoteContext";

const CATEGORY_TO_SERVICE = {
  "bathroom-tiling": { slug: "bathroom", label: "Bathroom & Wet Area Tiling" },
  "large-format-tiles": { slug: "commercial", label: "Large-Format & Commercial Tiling" },
  "tile-selection": { slug: "feature-walls", label: "Feature Walls & Tile Design" },
  "tiling-costs": { slug: "bathroom", label: "Bathroom & Wet Area Tiling" },
  "tile-preparation": { slug: "waterproofing", label: "Waterproofing & Preparation" },
  "renovation-tiling": { slug: "bathroom", label: "Bathroom & Renovation Tiling" },
  "natural-stone": { slug: "feature-walls", label: "Natural Stone & Feature Walls" },
  "porcelain": { slug: "kitchen", label: "Kitchen & Splashback Tiling" },
  "shower-tiling": { slug: "waterproofing", label: "Waterproofing & Shower Tiling" },
  "general-tiling-questions": { slug: "bathroom", label: "Bathroom & Wet Area Tiling" },
};

export default function GuideArticle() {
  const { slug } = useParams();
  const { openQuote } = useQuote();
  const categories = useGuideCategories();
  const [g, setG] = useState(undefined);

  useEffect(() => {
    window.scrollTo(0, 0);
    let on = true;
    api.get(`/guides/${slug}`).then((r) => on && setG(r.data)).catch(() => on && setG(null));
    return () => { on = false; };
  }, [slug]);

  if (g === undefined) return <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#9c7a4d]" /></div>;
  if (g === null) return <NotFound />;

  const catTitle = (categories.find((c) => c.slug === g.category) || {}).title || g.category;
  const title = g.meta_title || `${g.title} | Perth Tiling WA`;
  const description = g.meta_description || g.excerpt;

  // Parse inline markdown links [label](href) — internal paths use <Link>, external use <a>
  const renderInline = (text) => {
    const re = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let last = 0, m, k = 0;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) parts.push(text.slice(last, m.index));
      const [full, label, href] = m;
      const cls = "text-[#9c7a4d] underline underline-offset-4 decoration-[#9c7a4d]/40 hover:decoration-[#9c7a4d] transition-colors";
      if (href.startsWith("/")) {
        parts.push(<Link key={k++} to={href} className={cls} data-testid="guide-inline-link">{label}</Link>);
      } else {
        parts.push(<a key={k++} href={href} target="_blank" rel="noopener noreferrer" className={cls}>{label}</a>);
      }
      last = m.index + full.length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts.length ? parts : text;
  };

  const renderBody = (body) => {
    const blocks = (body || "").split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
    return blocks.map((block, i) => {
      if (block.startsWith("## ")) {
        return <h2 key={i} className="font-serif text-2xl md:text-3xl text-[#0f172a] font-light pt-4">{block.replace(/^##\s+/, "")}</h2>;
      }
      const lines = block.split("\n");
      if (lines.every((l) => l.trim().startsWith("- "))) {
        return (
          <ul key={i} className="list-disc pl-6 space-y-2 text-[#2f2b25] text-lg font-light leading-[1.7]">
            {lines.map((l, j) => <li key={j}>{renderInline(l.replace(/^-\s+/, ""))}</li>)}
          </ul>
        );
      }
      return <p key={i} className="text-[#2f2b25] text-lg font-light leading-[1.8]">{renderInline(block.replace(/\n/g, " "))}</p>;
    });
  };

  const relatedService = CATEGORY_TO_SERVICE[g.category];

  return (
    <SmoothScroll>
      <Seo
        pageKey="home"
        overrideTitle={title}
        overrideDescription={description}
        overrideKeywords={g.keywords}
        article={{ headline: g.title, datePublished: g.published_at, dateModified: g.updated_at }}
        faqs={g.faqs && g.faqs.length ? g.faqs : null}
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Tiling Guide", path: "/guide" }, { name: g.title, path: `/guide/${g.slug}` }]}
      />
      <div className="grain-overlay" aria-hidden />
      <div data-testid={`guide-article-${g.slug}`} className="bg-[#f8fafc] min-h-screen">
        <Navbar />

        <article className="pt-40 pb-20">
          <div className="mx-auto max-w-[820px] px-6 md:px-12">
            <nav className="mb-8 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#64748b]" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-[#9c7a4d] transition-colors">Home</Link><span>/</span>
              <Link to="/guide" className="hover:text-[#9c7a4d] transition-colors">Guide</Link><span>/</span>
              <span className="text-[#0f172a] truncate">{g.title}</span>
            </nav>
            <p className="overline !text-[#9c7a4d] mb-5">{catTitle}</p>
            <h1 className="font-serif font-light text-[#0f172a] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mb-8">
              <Reveal load>{g.title}</Reveal>
            </h1>
            {g.excerpt ? <p className="text-[#475569] text-xl font-light leading-relaxed mb-10">{g.excerpt}</p> : null}
          </div>

          {g.cover_image ? (
            <div className="mx-auto max-w-[1100px] px-6 md:px-12 mb-14">
              <div className="relative aspect-[16/9] overflow-hidden">
                <img src={resolveImg(g.cover_image)} alt={g.cover_alt || g.title} decoding="async" className="absolute inset-0 h-full w-full object-cover" />
              </div>
            </div>
          ) : null}

          <div className="mx-auto max-w-[820px] px-6 md:px-12">
            <div className="space-y-6">
              {renderBody(g.body)}
            </div>

            {g.faqs && g.faqs.length ? (
              <div className="mt-16">
                <h2 className="font-serif text-3xl text-[#0f172a] font-light mb-6">Frequently asked</h2>
                <Accordion type="single" collapsible className="w-full">
                  {g.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className="border-[#0f172a]/10">
                      <AccordionTrigger className="text-left text-[#0f172a] font-light text-lg hover:no-underline">{f.q}</AccordionTrigger>
                      <AccordionContent className="text-[#64748b] font-light leading-relaxed">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ) : null}

            <div className="mt-16 border-t border-[#0f172a]/10 pt-10 flex flex-wrap gap-4">
              <button onClick={openQuote} data-testid="guide-quote-cta" className="group inline-flex items-center gap-3 bg-[#9c7a4d] text-white hover:bg-[#0f172a] px-8 py-4 text-xs uppercase tracking-[0.22em] transition-colors">
                Get a Free Quote <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <a href={`tel:${CONTACT.phoneRaw}`} className="inline-flex items-center gap-3 border border-[#0f172a]/20 text-[#0f172a] hover:border-[#9c7a4d] hover:text-[#9c7a4d] px-8 py-4 text-xs uppercase tracking-[0.22em] transition-colors">
                <Phone className="h-4 w-4" /> {CONTACT.phone}
              </a>
            </div>

            {relatedService ? (
              <div className="mt-10 bg-[#f1f5f9] border border-[#0f172a]/10 p-6">
                <p className="overline !text-[#9c7a4d] mb-2">Related</p>
                <Link to={`/services/${relatedService.slug}`} data-testid="guide-related-service" className="group inline-flex items-center gap-2 font-serif text-xl text-[#0f172a] hover:text-[#9c7a4d] transition-colors">
                  {relatedService.label} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <p className="text-[#64748b] text-sm font-light mt-3">Serving Perth and suburbs including Dalkeith, Cottesloe, City Beach, Nedlands, Applecross and Fremantle.</p>
              </div>
            ) : null}

            <Link to="/guide" className="mt-10 inline-flex items-center gap-2 text-[#64748b] hover:text-[#9c7a4d] text-sm uppercase tracking-[0.2em] transition-colors">
              <ArrowLeft className="h-4 w-4" /> All guides
            </Link>
          </div>
        </article>

        <Footer />
      </div>
    </SmoothScroll>
  );
}
